import axios from "axios";
import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import PanelSkeleton from "../PanelSkeleton";
import Pagination from "../Pagination";
import { FaRegTrashAlt, FaStar } from "react-icons/fa";
import { MdOpenInNew } from "react-icons/md";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import mediaUrl from "../../utils/mediaUrl";
import styles from "../../styles/reviewspanel.module.css";

const STAR_CLASS = {
  1: styles.stars1,
  2: styles.stars2,
  3: styles.stars3,
  4: styles.stars4,
  5: styles.stars5,
};

const ReviewsPanel = () => {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [deleting, setDeleting] = useState(false);

  const [currentPage, setCurrentPage]   = useState(1);
  const [reviewsPerPage]                = useState(20);

  const token = useSelector(state => state.auth?.token);

  const fetchReviews = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/reviewsDashboard?page=${currentPage}&limit=${reviewsPerPage}`,
        { withCredentials: true, headers: { Authorization: `Bearer ${token}` } }
      );
      setData(res.data.data);
    } catch {
      toast.error(<span className="toast">لم نتمكن من تحميل البيانات. حاول مرة اخرى</span>);
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, reviewsPerPage]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const reviews     = data?.reviews    || [];
  const totalCount  = data?.totalCount || 0;
  const totalPages  = Math.ceil(totalCount / reviewsPerPage);

  const avgRating = reviews.length
    ? (reviews.reduce((sum, r) => sum + r.stars, 0) / reviews.length).toFixed(1)
    : '—';

  const handleDelete = (id) => {
    confirmAlert({
      message: `حذف التقييم؟`,
      buttons: [
        {
          label: 'نعم',
          onClick: async () => {
            setDeleting(true);
            try {
              await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/api/products/dashboard/deleteReview/${id}`,
                { withCredentials: true, headers: { Authorization: `Bearer ${token}` } }
              );
              toast.success(<span className="toast">تم حذف التقييم بنجاح</span>);
              await fetchReviews();
            } catch {
              toast.error(<span className="toast">لم نتمكن من حذف التقييم. حاول مرة اخرى</span>);
            } finally {
              setDeleting(false);
            }
          },
        },
        { label: 'لا' },
      ],
    });
  };

  if (loading) return <PanelSkeleton count={4} />;

  return (
    <div className={styles.pageWrapper}>
    <div className={styles.panel}>

      {/* ── Stats bar ── */}
      {data && (
        <div className={styles.statsBar}>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>إجمالى التقييمات</span>
            <span className={styles.statValue}>{totalCount}</span>
            <span className={styles.statSub}>تقييم مسجّل</span>
          </div>
          <div className={styles.statCard} style={{ animationDelay: '0.07s' }}>
            <span className={styles.statLabel}>متوسط التقييم</span>
            <span className={styles.statValue}>{avgRating}</span>
            <div className={styles.statStars}>
              {[1,2,3,4,5].map(n => (
                <FaStar
                  key={n}
                  className={`${styles.statStar} ${n <= Math.round(Number(avgRating)) ? styles.statStarFill : ''}`}
                />
              ))}
            </div>
          </div>
          <div className={styles.statCard} style={{ animationDelay: '0.14s' }}>
            <span className={styles.statLabel}>الصفحة الحالية</span>
            <span className={styles.statValue}>{reviews.length}</span>
            <span className={styles.statSub}>تقييم فى هذه الصفحة</span>
          </div>
        </div>
      )}

      {/* ── Review grid ── */}
      {reviews.length === 0 ? (
        <p className={styles.emptyMsg}>لا توجد تقييمات بعد</p>
      ) : (
        <>
          <div className={styles.grid}>
            {reviews.map((r, i) => {
              const dateStr = new Date(r.createdAt).toLocaleDateString('ar', {
                year: 'numeric', month: 'short', day: 'numeric',
              });

              return (
                <div
                  key={r.id}
                  className={`${styles.card} ${STAR_CLASS[r.stars] || ''}`}
                  style={{ animationDelay: `${i * 0.04}s` }}
                >
                  {/* Top: name + date + stars */}
                  <div className={styles.cardTop}>
                    <div className={styles.cardMeta}>
                      <p className={styles.reviewerName}>{r.name}</p>
                      <span className={styles.reviewDate}>{dateStr}</span>
                    </div>
                    <div className={styles.starsRow}>
                      {[1,2,3,4,5].map(n => (
                        <FaStar
                          key={n}
                          className={`${styles.star} ${n <= r.stars ? styles.starFill : ''}`}
                        />
                      ))}
                      <span className={styles.ratingNum}>{r.stars}/5</span>
                    </div>
                  </div>

                  {/* Review text */}
                  {r.text
                    ? <p className={styles.reviewText}>{r.text}</p>
                    : <p className={styles.noText}>لا يوجد نص للتقييم</p>
                  }

                  {/* Customer photos — open full size in a new tab */}
                  {r.images?.length > 0 && (
                    <div className={styles.reviewPhotos}>
                      {r.images.map((img) => (
                        <a key={img.id} href={mediaUrl(img.imageUrl)} target="_blank" rel="noreferrer">
                          <img src={mediaUrl(img.imageUrl)} alt="صورة من العميل" loading="lazy" />
                        </a>
                      ))}
                    </div>
                  )}

                  {/* Actions footer */}
                  <div className={styles.cardActions}>
                    <span className={styles.productIdTag}>
                      منتج&nbsp;
                      <span className={styles.productIdVal}>#{r.productId}</span>
                    </span>
                    <div className={styles.actionBtns}>
                      <a
                        href={`/product/${r.productId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.productBtn}
                      >
                        <MdOpenInNew />
                        المنتج
                      </a>
                      <button
                        className={styles.deleteBtn}
                        onClick={() => !deleting && handleDelete(r.id)}
                        disabled={deleting}
                      >
                        <FaRegTrashAlt />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              disabled={loading}
            />
          )}
        </>
      )}
    </div>
    </div>
  );
};

export default ReviewsPanel;
